import type {
  BlueprintOutputAccess,
  BlueprintLockedSection,
  GeneratedBlueprintOutput,
} from "@/features/blueprints/blueprintTypes";
import { detectIncompleteBuildPrompt } from "@shared/buildPromptQuality";

type BlueprintMarkdownInput = {
  title: string;
  generatedAt?: string | null;
  targetBuilder?: string | null;
  output: GeneratedBlueprintOutput;
  outputAccess?: BlueprintOutputAccess;
  projectName?: string | null;
  inputText?: string;
};

const lockedNotice =
  "_This section is included with Pro. Upgrade to unlock deeper build validation._";

function formatList(items: string[]): string {
  if (items.length === 0) {
    return "_No items were returned for this section._";
  }

  return items.map((item) => `- ${item}`).join("\n");
}

function formatDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function shortenInputText(text?: string): string | null {
  if (!text?.trim()) return null;
  const firstLine = text.split(/\n/)[0]?.trim() ?? "";
  const source = firstLine.length > 20 ? firstLine : text.trim();
  if (source.length <= 140) return source;
  return `${source.slice(0, 140).trimEnd()}…`;
}

export function createBlueprintMarkdown({
  title,
  generatedAt,
  targetBuilder,
  output,
  outputAccess,
  projectName,
  inputText,
}: BlueprintMarkdownInput): string {
  if (detectIncompleteBuildPrompt(output.builderReadyPrompt).incomplete) {
    throw new Error("This Master Build Prompt is incomplete. Regenerate before exporting it.");
  }
  const lockedSections = new Set(outputAccess?.lockedSections ?? []);
  const formatLockedOrList = (
    section: BlueprintLockedSection,
    items: string[],
  ) => {
    if (lockedSections.has(section)) {
      return lockedNotice;
    }

    return formatList(items);
  };

  const inputPreview = shortenInputText(inputText);

  const metadata = [
    projectName ? `- Project: ${projectName}` : null,
    inputPreview ? `- App idea: ${inputPreview}` : null,
    targetBuilder ? `- Target builder: ${targetBuilder}` : null,
    formatDate(generatedAt) ? `- Generated: ${formatDate(generatedAt)}` : null,
    `- Export type: Full Blueprint planning document`,
  ].filter(Boolean);

  return [
    `# ${title} - Full Blueprint`,
    "",
    metadata.length > 0 ? metadata.join("\n") : null,
    metadata.length > 0 ? "" : null,
    "## Plain-English Summary",
    output.plainEnglishSummary || "_No summary was returned._",
    "",
    "## Assumptions",
    formatList(output.assumptions),
    "",
    "## Not in Version 1",
    formatList(output.wontBuildYet),
    "",
    "## Product Specification",
    "",
    "### Product Goal",
    output.vibeRcSpec.vision || "_No vision was returned._",
    "",
    "### Screens & User Experience",
    output.vibeRcSpec.interface || "_No interface guidance was returned._",
    "",
    "### App Workflows",
    output.vibeRcSpec.behavior || "_No behavior guidance was returned._",
    "",
    "### Examples & Sample Data",
    output.vibeRcSpec.examples || "_No examples were returned._",
    "",
    "### Builder Guidance",
    output.vibeRcSpec.role || "_No role guidance was returned._",
    "",
    "### Version 1 Boundaries",
    output.vibeRcSpec.constraints || "_No constraints were returned._",
    "",
    "## Data Model",
    formatList(output.dataContentNeeded),
    "",
    "## Acceptance Criteria",
    formatLockedOrList("acceptanceCriteria", output.acceptanceCriteria),
    "",
    "## Edge Cases",
    formatLockedOrList("edgeCases", output.edgeCases),
    "",
    "## Test Scenarios",
    formatLockedOrList("testScenarios", output.testScenarios),
    "",
    "## Master Build Prompt",
    "",
    "_For AI builders, the dedicated Master Build Prompt download is the recommended file. This section is included so the planning document remains complete._",
    "",
    "```text",
    output.builderReadyPrompt || "",
    "```",
    "",
  ]
    .filter((part): part is string => part !== null)
    .join("\n");
}

type BuildPromptMarkdownInput = {
  title: string;
  generatedAt?: string | null;
  targetBuilder?: string | null;
  builderReadyPrompt: string;
};

export function createBuildPromptMarkdown({
  title,
  generatedAt,
  targetBuilder,
  builderReadyPrompt,
}: BuildPromptMarkdownInput): string {
  if (detectIncompleteBuildPrompt(builderReadyPrompt).incomplete) {
    throw new Error("This Master Build Prompt is incomplete. Regenerate before exporting it.");
  }

  return [
    `# ${title.trim() || "Alchemy App"} - Master Build Prompt`,
    "",
    targetBuilder ? `- Target builder: ${targetBuilder}` : null,
    formatDate(generatedAt) ? `- Generated: ${formatDate(generatedAt)}` : null,
    "- Use: Paste this Master Build Prompt into your AI builder as the first message.",
    "",
    builderReadyPrompt.trim(),
    "",
  ]
    .filter((part): part is string => part !== null)
    .join("\n");
}

export function createBuildPromptMarkdownFileName(
  title: string,
  date = new Date(),
): string {
  const safeTitle = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

  if (safeTitle) return `${safeTitle}-master-build-prompt.md`;
  return `alchemy-master-build-prompt-${date.toISOString().slice(0, 10)}.md`;
}

export function createBlueprintMarkdownFileName(title: string): string {
  const safeTitle = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

  return `alchemy-blueprint-${safeTitle || "v1"}-v1.md`;
}
